import {
  isPredefinedProfileId,
  makeId,
  type ProfileGeneration,
} from '../../utils/profiler-helper';
import {
  Channel,
  ChannelSuggestion,
  ComboBox,
  ComboBoxOption,
} from '@lmt/signal-viewer-ui';
import { useEffect, useMemo, useState } from 'react';
import { useChannelsProfile } from '../../contexts/ChannelsProfileContext';
import { useFileSession } from '../../contexts/FileSessionContext';
import { ProfileConfigurationButton } from './ProfileConfigurationButton';
import { Modal } from '../Modal';
import { PlusIcon } from '../icons/PlusIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { ChannelsEditor } from './ChannelsEditor';
import './ChannelsConfigInspector.css';

export const ChannelConfigInspector: React.FC = () => {
  const {
    profiles: availableProfiles,
    currentProfile,
    switchProfile: onProfileSwitch,
    saveProfile,
    deleteProfile: onProfileDelete,
  } = useChannelsProfile();
  const { availableSignalTypes, dataGeneration } = useFileSession();
  const currentGeneration: ProfileGeneration | null = dataGeneration;

  const [open, setOpen] = useState(false);
  const [newConfig, setNewConfig] = useState<Channel[]>(currentProfile.config);
  const [profileName, setProfileName] = useState(currentProfile.name);

  useEffect(() => {
    setProfileName(currentProfile.name);
  }, [currentProfile.name]);

  useEffect(() => {
    setNewConfig(currentProfile.config);
  }, [currentProfile.config]);

  const [selectedDevice, setSelectedDevice] = useState<
    ProfileGeneration | 'none'
  >('none');

  useEffect(() => {
    setSelectedDevice(currentGeneration ?? 'none');
  }, [currentGeneration]);

  const filteredProfiles = useMemo(
    () =>
      selectedDevice === 'none'
        ? availableProfiles
        : availableProfiles.filter(
            (profile) => profile.generation === selectedDevice,
          ),
    [availableProfiles, selectedDevice],
  );

  const profileOptions = useMemo<ComboBoxOption[]>(
    () =>
      filteredProfiles.map((profile) => ({
        value: profile.id,
        label: profile.name,
      })),
    [filteredProfiles],
  );

  const deviceOptions = useMemo<ComboBoxOption[]>(
    () => [
      { value: 'none', label: 'None' },
      { value: 'p28', label: 'P2.8' },
      { value: 'p3', label: 'P3' },
      { value: 'p32', label: 'pVent (P3.2)' },
      { value: 'p5', label: 'P5' },
    ],
    [],
  );

  const handleApply = () => {
    saveProfile({ ...currentProfile, config: newConfig, name: profileName });
    setOpen(false);
  };

  const handleClose = () => {
    setNewConfig(currentProfile.config);
    setProfileName(currentProfile.name);
    setOpen(false);
  };

  const handleCreateNewProfile = () => {
    const newId = makeId();
    onProfileSwitch(
      newId,
      selectedDevice === 'none'
        ? (currentGeneration ?? undefined)
        : selectedDevice,
    );
  };

  const handleDeleteProfile = () => {
    onProfileDelete(currentProfile.id);
  };

  const handleAddSuggestedChannel = (suggestion: ChannelSuggestion) => {
    const nextChannel: Channel = {
      ...structuredClone(suggestion.channel),
      id: makeId(),
    };

    setNewConfig((prev) => [...prev, nextChannel]);
  };

  return (
    <>
      <ProfileConfigurationButton onClick={() => setOpen(true)} />

      <Modal isOpen={open} onClose={handleClose}>
        <div className='channel-config-modal'>
          <div className='channel-config-header'>
            <div className='channel-config-header-top'>
              <h2 className='channel-config-title'>Profile configuration</h2>

              <button
                type='button'
                onClick={handleClose}
                className='channel-config-close'
                aria-label='Close'
              >
                ✕
              </button>
            </div>

            <div className='channel-config-profile-bar'>
              <div className='channel-config-profile-tabs'>
                <div className='channel-config-profile-tab'>PROFILE</div>

                {currentGeneration && (
                  <>
                    <div className='channel-config-profile-tab-divider' />
                    <div className='channel-config-profile-tab-name'>
                      {currentGeneration === 'p28'
                        ? 'P2.8'
                        : currentGeneration === 'p3'
                          ? 'P3'
                          : currentGeneration === 'p32'
                            ? 'pVent / P3.2'
                            : 'P5'}
                    </div>
                  </>
                )}
              </div>

              <div className='channel-config-profile-actions'>
                <button
                  type='button'
                  onClick={handleCreateNewProfile}
                  className='channel-config-ghost-button'
                >
                  <PlusIcon color={'#003568'} size={9} />
                  New profile
                </button>

                {!isPredefinedProfileId(currentProfile.id) && (
                  <>
                    <div className='channel-config-profile-action-divider' />

                    <button
                      type='button'
                      onClick={handleDeleteProfile}
                      className='channel-config-ghost-button'
                    >
                      <TrashIcon color={'#8B8B8B'} />
                      Delete profile
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className='channel-config-body'>
            <div className='channel-config-form-grid channel-config-form-grid--three-columns'>
              <div className='channel-config-field'>
                <label className='channel-config-label'>Device</label>
                <ComboBox
                  value={selectedDevice}
                  options={deviceOptions}
                  onChange={(value) =>
                    setSelectedDevice(value as ProfileGeneration | 'none')
                  }
                  className='channel-config-combobox'
                />
              </div>

              <div className='channel-config-field'>
                <label className='channel-config-label'>Profile</label>
                <ComboBox
                  value={
                    profileOptions.some(
                      (option) => option.value === currentProfile.id,
                    )
                      ? currentProfile.id
                      : ''
                  }
                  options={profileOptions}
                  onChange={onProfileSwitch}
                  className='channel-config-combobox'
                />
              </div>

              <div className='channel-config-field'>
                <label className='channel-config-label'>Profile name</label>
                <input
                  type='text'
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className='channel-config-input'
                />
              </div>
            </div>

            <ChannelsEditor
              config={newConfig}
              availableSignalTypes={availableSignalTypes}
              onChange={setNewConfig}
              onAddSuggestedChannel={handleAddSuggestedChannel}
            />
          </div>

          <div className='channel-config-footer'>
            <button
              type='button'
              onClick={handleClose}
              className='channel-config-secondary-button'
            >
              Cancel
            </button>

            <button
              type='button'
              onClick={handleApply}
              className='channel-config-primary-button'
            >
              Apply changes
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
